import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { PlusOutlined, ReloadOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import FetchAllEmployeesByCompany from '../services/FetchAllEmployeesByCompany';
import { fetchOnboardingHub, startOnboarding } from '../services/OnboardingService';
import './Onboarding.scss';

const { Title, Text } = Typography;

const statusColor = {
  not_started: 'default',
  in_progress: 'processing',
  pending_approval: 'warning',
  completed: 'success',
  cancelled: 'error',
};

function roleLabel(role) {
  return (role || '')
    .replace('ROLE_', '')
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export default function OnboardingHubPage() {
  const authContext = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  const token = authContext.token;
  const companyId = authContext.additionalUserInfo?.companyId;

  const employeesById = useMemo(() => {
    return employees.reduce((accumulator, employee) => {
      const employeeId = employee.id || employee.userId || employee.employeeId;
      if (employeeId) {
        accumulator[employeeId] = employee;
      }
      return accumulator;
    }, {});
  }, [employees]);

  const activeEmployeeIds = useMemo(() => new Set(items.map((item) => item.employeeId)), [items]);

  const mappedItems = useMemo(() => {
    return items.map((item) => {
      const employee = employeesById[item.employeeId] || {};
      return {
        ...item,
        employeeName:
          item.employeeName ||
          [employee.firstName, employee.lastName].filter(Boolean).join(' ') ||
          employee.name ||
          item.employeeId,
        employeeEmail: item.employeeEmail || employee.email || '—',
        role: item.role || employee.role || '—',
      };
    });
  }, [employeesById, items]);

  const stats = useMemo(() => {
    const total = mappedItems.length;
    const completed = mappedItems.filter((item) => item.status === 'completed').length;
    const pendingApproval = mappedItems.filter((item) => item.status === 'pending_approval').length;
    const inProgress = mappedItems.filter((item) => item.status === 'in_progress').length;
    return { total, completed, pendingApproval, inProgress };
  }, [mappedItems]);

  const fetchData = useCallback(async () => {
    if (!token || !companyId) return;
    setLoading(true);
    setError('');
    try {
      const [hubResponse, employeesResponse] = await Promise.all([
        fetchOnboardingHub(token, companyId),
        FetchAllEmployeesByCompany(token),
      ]);

      setItems(Array.isArray(hubResponse) ? hubResponse : []);
      setEmployees(Array.isArray(employeesResponse) ? employeesResponse : []);
    } catch (fetchError) {
      console.error(fetchError);
      setError('Failed to load onboarding data.');
    } finally {
      setLoading(false);
    }
  }, [token, companyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStart = async () => {
    try {
      const values = await form.validateFields();
      setStarting(true);

      await startOnboarding(token, {
        employeeId: values.employeeId,
        companyId,
        employeeRole: values.employeeRole,
        oscarChatEnabled: values.oscarChatEnabled !== false,
      });

      setModalOpen(false);
      form.resetFields();
      await fetchData();
      navigate(`/onboarding/${values.employeeId}`);
    } catch (submitError) {
      if (submitError?.errorFields) return;
      console.error(submitError);
      setError('Failed to start onboarding.');
    } finally {
      setStarting(false);
    }
  };

  const startableEmployees = employees.filter((employee) => {
    const employeeId = employee.id || employee.userId || employee.employeeId;
    return employeeId && !activeEmployeeIds.has(employeeId);
  });

  const columns = [
    {
      title: 'Employee',
      key: 'employee',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.employeeName}</Text>
          <Text type="secondary">{record.employeeEmail}</Text>
        </Space>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (value) => value === '—' ? '—' : roleLabel(value),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value) => <Tag color={statusColor[value] || 'default'}>{value || 'unknown'}</Tag>,
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_, record) => (
        <div style={{ minWidth: 180 }}>
          <Progress percent={record.percentComplete || 0} size="small" />
          <Text type="secondary">{record.completedSteps || 0}/{record.totalSteps || 0} steps</Text>
        </div>
      ),
    },
    {
      title: 'Started',
      dataIndex: 'startedDate',
      key: 'startedDate',
      render: (value) => value || '—',
    },
    {
      title: '',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Button type="link" icon={<ArrowRightOutlined />} onClick={() => navigate(`/onboarding/${record.employeeId}`)}>
          Open
        </Button>
      ),
    },
  ];

  return (
    <div className="onboarding-page">
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <div className="onboarding-hero">
          <div>
            <Title level={3} style={{ marginBottom: 8 }}>Onboarding Hub</Title>
            <Text type="secondary">
              Track progress, kick off new onboarding flows, and push employees into the right next step.
            </Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchData}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
              Start onboarding
            </Button>
          </Space>
        </div>

        {error && <Alert type="error" showIcon message={error} />}

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12} xl={6}><Card><Statistic title="Active" value={stats.total} /></Card></Col>
          <Col xs={24} md={12} xl={6}><Card><Statistic title="In Progress" value={stats.inProgress} /></Card></Col>
          <Col xs={24} md={12} xl={6}><Card><Statistic title="Pending Approval" value={stats.pendingApproval} /></Card></Col>
          <Col xs={24} md={12} xl={6}><Card><Statistic title="Completed" value={stats.completed} /></Card></Col>
        </Row>

        <Card className="onboarding-panel">
          {mappedItems.length === 0 && !loading ? (
            <Empty
              description="No onboarding flows yet"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button type="primary" onClick={() => setModalOpen(true)}>Start first onboarding</Button>
            </Empty>
          ) : (
            <Table
              rowKey={(record) => record.employeeId}
              loading={loading}
              dataSource={mappedItems}
              columns={columns}
              pagination={{ pageSize: 8 }}
              scroll={{ x: 900 }}
            />
          )}
        </Card>
      </Space>

      <Modal
        open={modalOpen}
        title="Start onboarding"
        onCancel={() => setModalOpen(false)}
        onOk={handleStart}
        okText="Start"
        okButtonProps={{ loading: starting }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ oscarChatEnabled: true }}>
          <Form.Item
            name="employeeId"
            label="Employee"
            rules={[{ required: true, message: 'Select an employee' }]}
          >
            <Select
              showSearch
              placeholder="Choose employee"
              optionFilterProp="label"
              options={startableEmployees.map((employee) => {
                const employeeId = employee.id || employee.userId || employee.employeeId;
                return {
                  value: employeeId,
                  label: `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || employee.email || employeeId,
                };
              })}
              onChange={(value) => {
                const employee = startableEmployees.find((item) => (item.id || item.userId || item.employeeId) === value);
                form.setFieldValue('employeeRole', employee?.role || 'ROLE_EMPLOYEE');
              }}
            />
          </Form.Item>
          <Form.Item
            name="employeeRole"
            label="Role"
            rules={[{ required: true, message: 'Role is required' }]}
          >
            <Select
              options={[
                { value: 'ROLE_EMPLOYEE', label: 'Employee' },
                { value: 'ROLE_FWORKER', label: 'Field Worker' },
                { value: 'ROLE_MANAGER', label: 'Manager' },
                { value: 'ROLE_CMANAGER', label: 'Company Manager' },
                { value: 'ROLE_FMANAGER', label: 'Finance Manager' },
              ]}
            />
          </Form.Item>
          <Form.Item name="oscarChatEnabled" label="Meet Oscar enabled">
            <Select
              options={[
                { value: true, label: 'Enabled' },
                { value: false, label: 'Disabled' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
